import { db } from "../firebaseConfig";
import {
  addDoc, collection, deleteDoc, doc, getDoc, getDocs,
  query, serverTimestamp, Timestamp, updateDoc, where,
} from "firebase/firestore";

/* ---------- utils ---------- */
function toIso(v) {
  if (!v) return null;
  if (v instanceof Timestamp) return v.toDate().toISOString();
  if (v instanceof Date) return v.toISOString();
  if (typeof v === "string") {
    const t = Date.parse(v);
    return Number.isFinite(t) ? new Date(t).toISOString() : null;
  }
  if (typeof v?.toDate === "function") return v.toDate().toISOString();
  return null;
}
function toTimestampOrNull(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? Timestamp.fromDate(new Date(t)) : null;
}
function toMs(v) {
  if (!v) return null;
  if (typeof v?.toDate === "function") return v.toDate().getTime();
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : null;
}
function mapPromoSnap(snap) {
  const d = snap.data();
  if (!d) return null;
  return {
    id: snap.id,
    title: d.title || "",
    subtitle: d.subtitle || "",
    ctaLabel: d.ctaLabel || "Voir",
    ctaHref: d.ctaHref || "/catalogue",
    imageUrl: d.imageUrl || "",
    theme: d.theme || "light",
    gradient: Array.isArray(d.gradient) ? d.gradient.slice(0, 2) : null,
    productIds: Array.isArray(d.productIds) ? d.productIds : [],
    limitProducts: Number.isFinite(d.limitProducts) ? d.limitProducts : 10,
    isEnabled: !!d.isEnabled,
    pinned: !!d.pinned,
    priority: Number.isFinite(d.priority) ? d.priority : 0,
    startsAt: toIso(d.startsAt),
    endsAt: toIso(d.endsAt),
    createdAt: toIso(d.createdAt),
    updatedAt: toIso(d.updatedAt),
    // ✅ IMPORTANT pour le placement
    placements: Array.isArray(d.placements) ? d.placements : [],
  };
}

/* ---------- PUBLIC: promo active (avec preview) ---------- */
export async function getActivePromo(previewId, _opts = {}) {
  try {
    if (previewId) {
      const snap = await getDoc(doc(db, "promos", String(previewId)));
      const p = snap.exists() ? mapPromoSnap(snap) : null;
      console.log("[promo] preview ->", p);
      return p;
    }

    const q = query(collection(db, "promos"), where("isEnabled", "==", true));
    const res = await getDocs(q);
    const all = res.docs.map(mapPromoSnap).filter(Boolean);

    const now = Date.now();
    const valids = all.filter(p => {
      const s = toMs(p.startsAt);
      const e = toMs(p.endsAt);
      const okStart = s == null || s <= now;
      const okEnd = e == null || now <= e;
      return okStart && okEnd;
    });

    valids.sort((a, b) => {
      if (Number(b.pinned) !== Number(a.pinned)) return Number(b.pinned) - Number(a.pinned);
      const ap = a.priority ?? 0, bp = b.priority ?? 0;
      if (bp !== ap) return bp - ap;
      const ac = toMs(a.createdAt) ?? 0, bc = toMs(b.createdAt) ?? 0;
      return bc - ac;
    });

    const chosen = valids[0] || null;
    console.log(`[promo] all=${all.length} valids=${valids.length} ->`, chosen);
    return chosen;
  } catch (e) {
    console.error("[promo] getActivePromo error:", e);
    return null;
  }
}

/* ---------- ADMIN: CRUD (tri côté client pour éviter index) ---------- */
export async function listPromos() {
  const res = await getDocs(collection(db, "promos"));
  const items = res.docs.map(mapPromoSnap).filter(Boolean);

  items.sort((a, b) => {
    if (Number(b.pinned) !== Number(a.pinned)) return Number(b.pinned) - Number(a.pinned);
    const ap = a.priority ?? 0, bp = b.priority ?? 0;
    if (bp !== ap) return bp - ap;
    const as = toMs(a.startsAt) ?? 0, bs = toMs(b.startsAt) ?? 0;
    if (bs !== as) return bs - as;
    const ac = toMs(a.createdAt) ?? 0, bc = toMs(b.createdAt) ?? 0;
    return bc - ac;
  });

  return { items, total: items.length };
}

export async function getPromo(id) {
  const snap = await getDoc(doc(db, "promos", String(id)));
  if (!snap.exists()) throw new Error("Promo introuvable");
  return mapPromoSnap(snap);
}

export async function savePromo(payload, id) {
  const data = {
    title: (payload.title || "").trim(),
    subtitle: (payload.subtitle || "").trim(),
    ctaLabel: (payload.ctaLabel || "Voir").trim(),
    ctaHref: (payload.ctaHref || "/catalogue").trim(),
    imageUrl: (payload.imageUrl || "").trim(),
    theme: payload.theme || "light",
    gradient: Array.isArray(payload.gradient) ? payload.gradient.slice(0, 2) : null,
    productIds: Array.isArray(payload.productIds) ? payload.productIds : [],
    limitProducts: Number(payload.limitProducts || 10),
    isEnabled: !!payload.isEnabled,
    pinned: !!payload.pinned,
    priority: Number(payload.priority || 0),
    startsAt: toTimestampOrNull(payload.startsAt),
    endsAt: toTimestampOrNull(payload.endsAt),
    updatedAt: serverTimestamp(),
    // ✅ on enregistre aussi les emplacements
    placements: Array.isArray(payload.placements) ? payload.placements : [],
  };

  if (id) {
    const ref = doc(db, "promos", String(id));
    await updateDoc(ref, data);
    const snap = await getDoc(ref);
    return mapPromoSnap(snap);
  } else {
    const ref = await addDoc(collection(db, "promos"), { ...data, createdAt: serverTimestamp() });
    const snap = await getDoc(ref);
    return mapPromoSnap(snap);
  }
}

export async function deletePromo(id) {
  await deleteDoc(doc(db, "promos", String(id)));
  return { ok: true };
}

/* ---------- Par placement ---------- */
function isWithinWindow(p) {
  const now = Date.now();
  const toM = (v) =>
    typeof v?.toDate === "function" ? v.toDate().getTime()
    : Number.isFinite(Date.parse(v)) ? Date.parse(v)
    : null;
  const s = toM(p.startsAt);
  const e = toM(p.endsAt);
  const okStart = s == null || s <= now;
  const okEnd = e == null || now <= e;
  return okStart && okEnd;
}
function sortPromos(a, b) {
  if (Number(b.pinned) !== Number(a.pinned)) return Number(b.pinned) - Number(a.pinned);
  const ap = a.priority ?? 0, bp = b.priority ?? 0;
  if (bp !== ap) return bp - ap;
  const t = (v) =>
    typeof v?.toDate === "function" ? v.toDate().getTime()
    : Number.isFinite(Date.parse(v)) ? Date.parse(v)
    : 0;
  return (t(b.createdAt) ?? 0) - (t(a.createdAt) ?? 0);
}

export async function listActivePromosByPlacement(placement, take = 1) {
  try {
    const res = await getDocs(collection(db, "promos"));
    const all = res.docs.map(mapPromoSnap).filter(Boolean);

    const filtered = all
      .filter(p => p.isEnabled)
      .filter(p => Array.isArray(p.placements) && p.placements.includes(placement))
      .filter(isWithinWindow)
      .sort(sortPromos);

    return filtered.slice(0, Math.max(1, take));
  } catch (e) {
    console.error("[promo] listActivePromosByPlacement error:", e);
    return [];
  }
}

export async function getActivePromoForPlacement(placement) {
  const list = await listActivePromosByPlacement(placement, 1);
  return list[0] || null;
}
